import { forwardRef, type InputHTMLAttributes, type ReactNode, type TextareaHTMLAttributes } from 'react';
import { cn } from '../../lib/utils';

const fieldBase =
  'w-full rounded-control border bg-surface-2 text-text placeholder:text-faint transition-[border-color,box-shadow] duration-150 outline-none focus:border-brand-blue-2 focus:shadow-[0_0_0_3px_rgba(37,99,235,0.25)] disabled:opacity-50 disabled:cursor-not-allowed';

export interface InputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size' | 'prefix'> {
  label?: ReactNode;
  hint?: ReactNode;
  error?: ReactNode;
  leftIcon?: ReactNode;
  rightSlot?: ReactNode;
  /** Text shown before the field inside the border, e.g. "/" for aliases. */
  prefix?: ReactNode;
  mono?: boolean;
  fieldSize?: 'md' | 'lg';
  wrapperClassName?: string;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { label, hint, error, leftIcon, rightSlot, prefix, mono, fieldSize = 'md', className, wrapperClassName, id, ...rest },
  ref,
) {
  const inputId = id || (typeof label === 'string' ? `f-${label.toLowerCase().replace(/\s+/g, '-')}` : undefined);
  return (
    <div className={cn('flex flex-col gap-1.5', wrapperClassName)}>
      {label && (
        <label htmlFor={inputId} className="text-[13px] font-medium text-text-2">
          {label}
          {hint && <span className="ml-1 font-normal text-faint">{hint}</span>}
        </label>
      )}
      <div className="relative flex items-stretch">
        {prefix && (
          <span className="flex items-center rounded-l-control border border-r-0 border-border bg-surface-3 px-3 font-mono text-sm text-faint">
            {prefix}
          </span>
        )}
        {leftIcon && (
          <span className="pointer-events-none absolute inset-y-0 left-4 flex items-center text-faint [&>svg]:size-[18px]">
            {leftIcon}
          </span>
        )}
        <input
          ref={ref}
          id={inputId}
          aria-invalid={error ? true : undefined}
          className={cn(
            fieldBase,
            fieldSize === 'lg' ? 'h-13 px-5 text-base' : 'h-11 px-4 text-sm',
            leftIcon && 'pl-11',
            rightSlot && 'pr-24',
            prefix && 'rounded-l-none',
            mono && 'font-mono',
            error ? 'border-danger' : 'border-border',
            className,
          )}
          {...rest}
        />
        {rightSlot && <span className="absolute inset-y-0 right-3 flex items-center text-[13px]">{rightSlot}</span>}
      </div>
      {error && (
        <p role="alert" className="text-[13px] text-danger">
          {error}
        </p>
      )}
    </div>
  );
});

export interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: ReactNode;
  hint?: ReactNode;
  error?: ReactNode;
  wrapperClassName?: string;
}

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(function Textarea(
  { label, hint, error, className, wrapperClassName, id, rows = 3, ...rest },
  ref,
) {
  const inputId = id || (typeof label === 'string' ? `f-${label.toLowerCase().replace(/\s+/g, '-')}` : undefined);
  return (
    <div className={cn('flex flex-col gap-1.5', wrapperClassName)}>
      {label && (
        <label htmlFor={inputId} className="text-[13px] font-medium text-text-2">
          {label}
          {hint && <span className="ml-1 font-normal text-faint">{hint}</span>}
        </label>
      )}
      <textarea
        ref={ref}
        id={inputId}
        rows={rows}
        aria-invalid={error ? true : undefined}
        className={cn(fieldBase, 'px-4 py-3 text-sm', error ? 'border-danger' : 'border-border', className)}
        {...rest}
      />
      {error && (
        <p role="alert" className="text-[13px] text-danger">
          {error}
        </p>
      )}
    </div>
  );
});
